import React, { useRef, useState } from "react";
import {
  Upload, FileDown, AlertTriangle, CheckCircle2, X, Loader2,
  FileSpreadsheet, Info,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { gbp } from "../lib/format";
import { PrimaryButton, QuietButton, Modal } from "./UI";

const cls = (...a) => a.filter(Boolean).join(" ");

/* =========================================================================
 * LT-27 — Master Library CSV Import Modal
 *
 * Three-step flow:
 *   1) File pick + duplicate strategy + allow-unknown-units toggle
 *   2) Validate (server parses, returns preview payload; writes nothing)
 *   3) Commit (server re-parses the SAME file — no JSON round-trip)
 *
 * If the user cancels at any point BEFORE step 3 completes, nothing is
 * written and no batch document persists.
 *
 * `errors.csv` is generated CLIENT-SIDE from the validate response so we
 * never need a server-side batch lookup for rejected rows.
 * ========================================================================= */
export function LibraryImportModal({ open, onClose, onCommitted }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [strategy, setStrategy] = useState("skip");
  const [allowUnknownUnits, setAllowUnknownUnits] = useState(false);
  const [validating, setValidating] = useState(false);
  const [preview, setPreview] = useState(null);   // response from /validate
  const [committing, setCommitting] = useState(false);

  const reset = () => {
    setFile(null);
    setStrategy("skip");
    setAllowUnknownUnits(false);
    setPreview(null);
    setValidating(false);
    setCommitting(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => {
    if (validating || committing) return;
    reset();
    onClose?.();
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/library/import/template", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "luxtender_master_library_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const runValidate = async () => {
    if (!file) {
      toast.error("Please choose a CSV file");
      return;
    }
    setValidating(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("duplicate_strategy", strategy);
      fd.append("allow_unknown_units", String(allowUnknownUnits));
      const r = await api.post("/library/import/validate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  const runCommit = async () => {
    if (!file || !preview) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("duplicate_strategy", strategy);
      fd.append("allow_unknown_units", String(allowUnknownUnits));
      const r = await api.post("/library/import/commit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Imported ${r.data.imported} draft item${r.data.imported === 1 ? "" : "s"}` +
          (r.data.skipped ? ` · ${r.data.skipped} skipped` : "") +
          (r.data.rejected ? ` · ${r.data.rejected} rejected` : "")
      );
      onCommitted?.(r.data);
      reset();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const downloadErrorsCsv = () => {
    if (!preview || !preview.rejected?.length) return;
    // Build a CSV client-side. Include ALL original columns + row_number + error_reason.
    const rows = preview.rejected;
    const originalKeys = new Set();
    rows.forEach((r) => Object.keys(r.original || {}).forEach((k) => originalKeys.add(k)));
    const headerCols = ["row_number", ...Array.from(originalKeys), "error_reason"];
    const escape = (v) => {
      if (v === undefined || v === null) return "";
      const s = String(v);
      if (/[",\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const lines = [headerCols.join(",")];
    for (const r of rows) {
      const row = [String(r.row_number)];
      for (const k of headerCols.slice(1, -1)) row.push(escape(r.original?.[k]));
      row.push(escape(r.reason));
      lines.push(row.join(","));
    }
    const bom = "\ufeff";
    const csvText = bom + lines.join("\r\n");
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const base = (file?.name || "import").replace(/\.csv$/i, "");
    a.download = `${base}__errors.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ---------------- Step 1: pick + configure ---------------- */
  const step1 = !preview;

  /* ---------------- render ---------------- */

  return (
    <Modal
      open={open}
      onClose={close}
      title={step1 ? "Import CSV to Master Library" : "Review before importing"}
      maxWidth={900}
      testid="library-import-modal"
      footer={
        step1 ? (
          <>
            <QuietButton onClick={close} data-testid="import-cancel">
              Cancel
            </QuietButton>
            <PrimaryButton
              onClick={runValidate}
              disabled={!file || validating}
              icon={validating ? Loader2 : Upload}
              data-testid="import-validate"
            >
              {validating ? "Validating…" : "Validate file"}
            </PrimaryButton>
          </>
        ) : (
          <>
            <QuietButton
              onClick={() => setPreview(null)}
              disabled={committing}
              data-testid="import-back"
            >
              Back
            </QuietButton>
            <QuietButton onClick={close} disabled={committing} data-testid="import-cancel-preview">
              Cancel
            </QuietButton>
            <PrimaryButton
              onClick={runCommit}
              disabled={committing || preview.valid_count === 0}
              icon={committing ? Loader2 : CheckCircle2}
              data-testid="import-commit"
            >
              {(() => {
                if (committing) return "Importing…";
                const unknownSkip = preview.allow_unknown_units ? 0 : (preview.unknown_unit_would_skip || 0);
                const dupSkip = strategy === "skip" ? preview.duplicate_count : 0;
                const n = Math.max(preview.valid_count - dupSkip - unknownSkip, 0);
                return `Import ${n} item${n === 1 ? "" : "s"} as Draft`;
              })()}
            </PrimaryButton>
          </>
        )
      }
    >
      {step1 ? (
        <Step1
          file={file}
          setFile={setFile}
          inputRef={inputRef}
          strategy={strategy}
          setStrategy={setStrategy}
          allowUnknownUnits={allowUnknownUnits}
          setAllowUnknownUnits={setAllowUnknownUnits}
          onDownloadTemplate={downloadTemplate}
        />
      ) : (
        <Step2Preview
          preview={preview}
          strategy={strategy}
          onDownloadErrors={downloadErrorsCsv}
        />
      )}
    </Modal>
  );
}

/* ---------------- Step 1 body ---------------- */

function Step1({
  file, setFile, inputRef,
  strategy, setStrategy,
  allowUnknownUnits, setAllowUnknownUnits,
  onDownloadTemplate,
}) {
  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error("File must be a .csv");
      return;
    }
    setFile(f);
  };

  return (
    <div className="space-y-6" data-testid="library-import-step1">
      {/* Intro band */}
      <div
        className="flex items-start gap-3 px-4 py-3 rounded-md border"
        style={{ background: "#E0EAF1", borderColor: "#B6C9DA" }}
      >
        <Info size={14} className="text-info mt-0.5 flex-shrink-0" />
        <div style={{ fontSize: 12, color: "#173E5C" }}>
          Imported items land as <strong>Draft</strong>. They will not appear
          in any tender&apos;s library picker until you review them and promote to
          Live from the Master Library. Reference rates are <strong>display-only</strong>
          — they are never used in tender pricing.
        </div>
      </div>

      {/* File picker */}
      <div>
        <label
          className="block col-head text-ink-3 mb-2"
          style={{ fontSize: 10.5 }}
        >
          CSV file
        </label>
        <div className="flex items-center gap-3">
          <label
            className={cls(
              "flex-1 h-10 px-3 flex items-center gap-2 rounded-md border cursor-pointer",
              file ? "border-line bg-surface" : "border-dashed border-line bg-surface-2/40 hover:bg-surface-2"
            )}
            data-testid="import-file-picker"
          >
            <FileSpreadsheet size={14} className="text-ink-3" />
            <span
              className={cls("truncate", file ? "text-ink" : "text-ink-3")}
              style={{ fontSize: 12.5 }}
            >
              {file ? file.name : "Choose a .csv file"}
            </span>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onFileChange}
              className="hidden"
              data-testid="import-file-input"
            />
          </label>
          <QuietButton
            onClick={onDownloadTemplate}
            icon={FileDown}
            data-testid="import-download-template"
          >
            Download template
          </QuietButton>
        </div>
        {file && (
          <p className="text-ink-4 mt-1.5" style={{ fontSize: 11 }}>
            {(file.size / 1024).toFixed(1)} KB · Max 5 MB · Max 5,000 rows
          </p>
        )}
      </div>

      {/* Duplicate strategy */}
      <div>
        <label
          className="block col-head text-ink-3 mb-2"
          style={{ fontSize: 10.5 }}
        >
          If a description already exists
        </label>
        <div className="space-y-2">
          {[
            {
              key: "skip",
              label: "Skip existing",
              hint: "Rows matching an existing item (draft or live) are skipped. RECOMMENDED default.",
            },
            {
              key: "update",
              label: "Update existing",
              hint: "Only reference fields, frequency, source refs and notes are updated. Recipes, rates and phase tags are preserved.",
            },
            {
              key: "insert",
              label: "Insert anyway",
              hint: "Always create a new item, allowing duplicates.",
            },
          ].map((opt) => (
            <label
              key={opt.key}
              className={cls(
                "flex items-start gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-colors",
                strategy === opt.key ? "border-accent bg-accent-soft" : "border-line hover:bg-surface-2/40"
              )}
              data-testid={`import-strategy-${opt.key}`}
            >
              <input
                type="radio"
                name="strategy"
                value={opt.key}
                checked={strategy === opt.key}
                onChange={() => setStrategy(opt.key)}
                className="mt-1 flex-shrink-0 accent-accent"
              />
              <div>
                <div className="text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>
                  {opt.label}
                  {opt.key === "skip" && (
                    <span
                      className="ml-2 font-mono uppercase"
                      style={{ fontSize: 9, letterSpacing: "0.06em", color: "#6B6B6B" }}
                    >
                      DEFAULT
                    </span>
                  )}
                </div>
                <div className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
                  {opt.hint}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Allow unknown units */}
      <label
        className="flex items-start gap-3 px-3 py-2.5 rounded-md border border-line hover:bg-surface-2/40 cursor-pointer"
        data-testid="import-allow-unknown-units"
      >
        <input
          type="checkbox"
          checked={allowUnknownUnits}
          onChange={(e) => setAllowUnknownUnits(e.target.checked)}
          className="mt-1 flex-shrink-0 accent-accent"
        />
        <div>
          <div className="text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>
            Import unknown units anyway
          </div>
          <div className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
            Rows with units not on the accepted list will always appear in the
            preview as warnings — never rejected. When this box is <strong>ticked</strong>,
            they import with the unit preserved verbatim. When <strong>unticked</strong>,
            they are skipped at commit time.
          </div>
        </div>
      </label>
    </div>
  );
}

/* ---------------- Step 2 body: preview ---------------- */

function Step2Preview({ preview, strategy, onDownloadErrors }) {
  const unknownSkip = preview.allow_unknown_units ? 0 : (preview.unknown_unit_would_skip || 0);
  const dupSkip = strategy === "skip" ? preview.duplicate_count : 0;
  const willImport = Math.max(preview.valid_count - dupSkip - unknownSkip, 0);
  return (
    <div className="space-y-5" data-testid="library-import-step2">
      {/* Counts strip */}
      <div className="grid grid-cols-4 gap-3">
        <CountTile label="Total rows" value={preview.total_rows} tone="ink" />
        <CountTile label="Will import" value={willImport} tone="success" />
        <CountTile label="Duplicates" value={preview.duplicate_count} tone="warn" />
        <CountTile label="Rejected" value={preview.rejected_count} tone="error" />
      </div>

      {/* Warnings */}
      {preview.unknown_units?.length > 0 && (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-md border bg-warning-soft"
          style={{ borderColor: "#F0D9A8" }}
        >
          <AlertTriangle size={14} className="text-warning mt-0.5 flex-shrink-0" />
          <div style={{ fontSize: 12, color: "#7A3E06" }}>
            <strong>{preview.unknown_units.length} unknown unit value{preview.unknown_units.length === 1 ? "" : "s"}:</strong>{" "}
            {preview.unknown_units.map((u) => (
              <span
                key={u}
                className="inline-block font-mono px-1.5 py-0.5 mx-0.5 rounded"
                style={{ background: "#FDECC8", fontSize: 10.5 }}
              >
                {u}
              </span>
            ))}
            .{" "}
            {preview.allow_unknown_units ? (
              <>
                All <strong>{preview.unknown_unit_count}</strong> row{preview.unknown_unit_count === 1 ? "" : "s"}{" "}
                using {preview.unknown_unit_count === 1 ? "this" : "these"} value{preview.unknown_unit_count === 1 ? "" : "s"}{" "}
                will import with the unit preserved verbatim.
              </>
            ) : (
              <>
                <strong>{preview.unknown_unit_would_skip}</strong> row{preview.unknown_unit_would_skip === 1 ? "" : "s"} will be{" "}
                <strong>skipped</strong> unless you tick &ldquo;Import unknown units anyway&rdquo; before committing.
              </>
            )}
          </div>
        </div>
      )}
      {preview.unmatched_phase_tags?.length > 0 && (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-md border bg-warning-soft"
          style={{ borderColor: "#F0D9A8" }}
        >
          <AlertTriangle size={14} className="text-warning mt-0.5 flex-shrink-0" />
          <div style={{ fontSize: 12, color: "#7A3E06" }}>
            <strong>{preview.unmatched_phase_tags.length} unmatched phase tag{preview.unmatched_phase_tags.length === 1 ? "" : "s"}:</strong>{" "}
            {preview.unmatched_phase_tags.map((t) => (
              <span
                key={t}
                className="inline-block font-mono px-1.5 py-0.5 mx-0.5 rounded"
                style={{ background: "#FDECC8", fontSize: 10.5 }}
              >
                {t}
              </span>
            ))}
            . These rows will import as <strong>Unassigned</strong> — tag them from the Draft view.
          </div>
        </div>
      )}

      {/* Preview table — first 10 valid rows */}
      {preview.preview?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="col-head text-ink-2" style={{ fontSize: 10.5 }}>
              First {preview.preview.length} row{preview.preview.length === 1 ? "" : "s"} to import
            </h3>
            <span className="text-ink-4" style={{ fontSize: 11 }}>
              of {preview.valid_count - preview.duplicate_count} fresh
            </span>
          </div>
          <div className="rounded-md border border-line overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-surface-2/60 border-b border-line">
                  <th className="col-head px-3 py-2" style={{ width: 44 }}>Row</th>
                  <th className="col-head px-3 py-2">Description</th>
                  <th className="col-head px-3 py-2" style={{ width: 60 }}>Unit</th>
                  <th className="col-head px-3 py-2" style={{ width: 130 }}>Phase</th>
                  <th className="col-head px-3 py-2 text-right" style={{ width: 90 }}>Ref £</th>
                  <th className="col-head px-3 py-2 text-right" style={{ width: 60 }}>Freq</th>
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((r) => (
                  <tr
                    key={r.row_number}
                    className="border-b border-line-soft last:border-0"
                    data-testid={`import-preview-row-${r.row_number}`}
                  >
                    <td className="px-3 py-2 font-mono tnum text-ink-4" style={{ fontSize: 11 }}>{r.row_number}</td>
                    <td className="px-3 py-2 text-ink" style={{ fontSize: 12.5 }}>{r.description}</td>
                    <td className="px-3 py-2 font-mono tnum text-ink-3" style={{ fontSize: 11.5 }}>
                      {r.unit}
                      {r.unit_unknown && (
                        <AlertTriangle size={11} className="text-warning inline-block ml-1" />
                      )}
                    </td>
                    <td className="px-3 py-2 text-ink-3" style={{ fontSize: 11.5 }}>
                      {r.phase_tag || <span className="text-ink-4 italic">Unassigned</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tnum text-ink" style={{ fontSize: 12 }}>
                      {r.ref_rate_total != null ? gbp(r.ref_rate_total) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tnum text-ink-3" style={{ fontSize: 11.5 }}>
                      {r.frequency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rejected rows */}
      {preview.rejected_count > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="col-head text-ink-2" style={{ fontSize: 10.5 }}>
              Rejected rows ({preview.rejected_count})
            </h3>
            <QuietButton
              onClick={onDownloadErrors}
              icon={FileDown}
              data-testid="import-download-errors"
            >
              Download errors.csv
            </QuietButton>
          </div>
          <div
            className="rounded-md border max-h-56 overflow-y-auto"
            style={{ borderColor: "#F4C7C7", background: "#FDECEC66" }}
          >
            <table className="w-full text-left">
              <thead
                className="sticky top-0 border-b"
                style={{ background: "#FDECEC", borderColor: "#F4C7C7" }}
              >
                <tr>
                  <th className="col-head px-3 py-2" style={{ width: 44, color: "#7F1D1D" }}>Row</th>
                  <th className="col-head px-3 py-2" style={{ color: "#7F1D1D" }}>Description (as sent)</th>
                  <th className="col-head px-3 py-2" style={{ color: "#7F1D1D" }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {preview.rejected.map((r) => (
                  <tr
                    key={r.row_number}
                    className="border-b last:border-0"
                    style={{ borderColor: "#F4C7C7" }}
                    data-testid={`import-rejected-row-${r.row_number}`}
                  >
                    <td className="px-3 py-2 font-mono tnum" style={{ fontSize: 11, color: "#7F1D1D" }}>{r.row_number}</td>
                    <td className="px-3 py-2 text-ink" style={{ fontSize: 12.5 }}>
                      {r.original?.description || <span className="italic text-ink-4">(blank)</span>}
                    </td>
                    <td className="px-3 py-2" style={{ fontSize: 12, color: "#7F1D1D" }}>{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Duplicates preview */}
      {preview.duplicate_count > 0 && (
        <div>
          <h3 className="col-head text-ink-2 mb-2" style={{ fontSize: 10.5 }}>
            Duplicates ({preview.duplicate_count})
          </h3>
          <div className="text-ink-3 mb-2" style={{ fontSize: 12 }}>
            {strategy === "skip" && "These rows will be SKIPPED."}
            {strategy === "update" && "These rows will UPDATE the matched item's reference data only."}
            {strategy === "insert" && "These rows will BE INSERTED, creating duplicates."}
          </div>
          {preview.duplicates_preview?.length > 0 && (
            <div className="rounded-md border border-line overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-surface-2/60 border-b border-line">
                    <th className="col-head px-3 py-2" style={{ width: 44 }}>Row</th>
                    <th className="col-head px-3 py-2">Description</th>
                    <th className="col-head px-3 py-2" style={{ width: 90 }}>Matched status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.duplicates_preview.map((r) => (
                    <tr
                      key={r.row_number}
                      className="border-b border-line-soft last:border-0"
                      data-testid={`import-duplicate-row-${r.row_number}`}
                    >
                      <td className="px-3 py-2 font-mono tnum text-ink-4" style={{ fontSize: 11 }}>{r.row_number}</td>
                      <td className="px-3 py-2 text-ink" style={{ fontSize: 12.5 }}>{r.description}</td>
                      <td className="px-3 py-2 font-mono uppercase text-ink-3" style={{ fontSize: 10.5, letterSpacing: "0.05em" }}>
                        {r._duplicate_of?.status || "live"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CountTile({ label, value, tone }) {
  const styles = {
    ink:     { bg: "#F5F3EE",   border: "#E2DDD2", fg: "#1A1A1A" },
    success: { bg: "#E8EFE9",   border: "#C7D8CC", fg: "#23402F" },
    warn:    { bg: "#FEF3E2",   border: "#F0D9A8", fg: "#7A3E06" },
    error:   { bg: "#FDECEC",   border: "#F4C7C7", fg: "#7F1D1D" },
  }[tone];
  return (
    <div
      className="rounded-md border px-3 py-2.5"
      style={{ background: styles.bg, borderColor: styles.border, color: styles.fg }}
    >
      <div className="col-head" style={{ fontSize: 9.5 }}>{label}</div>
      <div className="font-display tnum mt-0.5" style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
    </div>
  );
}
